import { getHttpClient } from '@spcc/core'
import { SAP_DASHBOARD_ENDPOINTS } from '../../domain/constants'
import {
  transformSyncJobDtoToDomain,
  transformDlqEntryDtoToDomain,
  transformDashboardStatusToDomain,
} from '../../api/transformers'
import type {
  SapDlqEntry,
  SapDashboardStatus,
  SapSyncFilter,
  SyncJobsResponse,
  PaginationMeta,
} from '../../domain/types'

function addEntityIdFilters(params: URLSearchParams, entityIds: string[] | undefined): void {
  if (!entityIds || entityIds.length === 0) {
    return
  }

  entityIds.forEach((entityId, index) => {
    params.set(`filters[$or][${index}][entityId][$eq]`, entityId)
  })
}

function addSapOperationFilters(params: URLSearchParams, operations: string[] | undefined): void {
  if (!operations || operations.length === 0) {
    return
  }

  operations.forEach((op, index) => {
    params.set(`filters[sapOperation][$in][${index}]`, op)
  })
}

export const sapSyncApi = {
  async getDashboardStatus(): Promise<SapDashboardStatus> {
    const http = getHttpClient()
    const response = await http.get<unknown>(SAP_DASHBOARD_ENDPOINTS.STATUS)
    return transformDashboardStatusToDomain(response.data)
  },

  async getSyncJobs(filter?: SapSyncFilter): Promise<SyncJobsResponse> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    if (filter?.direction) params.set('filters[direction][$eq]', filter.direction)
    if (filter?.entityType) params.set('filters[entityType][$eq]', filter.entityType)
    addEntityIdFilters(params, filter?.entityIds)
    if (filter?.sapOperations && filter.sapOperations.length > 0) {
      addSapOperationFilters(params, filter.sapOperations)
    } else if (filter?.sapOperation) {
      params.set('filters[sapOperation][$eq]', filter.sapOperation)
    }
    if (filter?.status) params.set('filters[jobStatus][$eq]', filter.status)
    if (filter?.createdDateFrom) params.set('filters[createdAt][$gte]', filter.createdDateFrom)
    if (filter?.createdDateTo) params.set('filters[createdAt][$lte]', filter.createdDateTo)
    if (filter?.page) params.set('pagination[page]', String(filter.page))
    if (filter?.pageSize) params.set('pagination[pageSize]', String(filter.pageSize))
    const sortField = filter?.sortField || 'createdAt'
    const sortOrder = filter?.sortOrder || 'desc'
    params.set('sort', `${sortField}:${sortOrder}`)

    const url = `${SAP_DASHBOARD_ENDPOINTS.SYNC_JOBS}?${params.toString()}`
    const response = await http.get<unknown>(url)
    const responseData = response.data as Record<string, unknown>
    const raw = Array.isArray(responseData?.data) ? responseData.data : []
    const jobs = (raw as unknown[]).map(transformSyncJobDtoToDomain)

    const meta = responseData?.meta as Record<string, unknown> | undefined
    const paginationData = meta?.pagination as Record<string, unknown> | undefined

    const pagination: PaginationMeta = {
      page: (paginationData?.page as number) ?? filter?.page ?? 1,
      pageSize: (paginationData?.pageSize as number) ?? filter?.pageSize ?? 25,
      pageCount: (paginationData?.pageCount as number) ?? 1,
      total: (paginationData?.total as number) ?? jobs.length,
    }

    return { data: jobs, meta: pagination }
  },

  async getDlqEntries(): Promise<SapDlqEntry[]> {
    const http = getHttpClient()
    const response = await http.get<unknown>(SAP_DASHBOARD_ENDPOINTS.DLQ)
    const responseData = response.data as Record<string, unknown>
    const raw = Array.isArray(responseData?.data) ? responseData.data : []
    return (raw as unknown[]).map(transformDlqEntryDtoToDomain)
  },

  async retryDlqEntry(id: number): Promise<void> {
    const http = getHttpClient()
    await http.post(SAP_DASHBOARD_ENDPOINTS.DLQ_RETRY(id))
  },
}
